<template>
  <div>
    <BaseListPage
        :name="name"
        :no-condition="false"
        :base-query="baseQuery"
        :store-path="storePath"
        :response-path="responsePath"
        :save-key="saveKey"
        :table-set-list="tableSetList"
        :table-data-list="sortedList"
        title="在庫調整指示一覧"
        title-class="stockAdjustment-header"
        icon="stock">
      <span slot="headerAction"
          class="action_button">
        <el-dropdown
            v-show="$isFunctionShown(define.PermissionKeys.Stock.Adjustment.List.NEW)"
            @command="actionCommand">
          <el-button size="small">
            アクション
            <i class="el-icon-arrow-down el-icon--right" />
          </el-button>
          <el-dropdown-menu
              slot="dropdown"
              class="header-action-menu">
            <el-dropdown-item
                :disabled="$isFunctionDisabled(define.PermissionKeys.Stock.Adjustment.List.NEW)"
                :command="define.STOCK_ALERT_MODE.NEW">
              新規登録
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </span>
      <template
          slot="tableOperation"
          slot-scope="{ row }">
        <BaseOperationTooltip
            v-if="showTooltip(row.row)"
            :list="getTooltipList(row.row)"
            class="operation-button"
            placement="bottom-end" />
      </template>
    </BaseListPage>
    <!-- 在庫調整ダイアログ -->
    <StockAdjustmentDialog
        :visible.sync="dialogVisible"
        @refreshList="() => refreshList()" />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapMutations, mapGetters } from 'vuex'

import common from '@/utils/common'
import message from '@/utils/message'
import * as config from '@/config'

import * as define from '@/define'
import * as query from '@/queries/stock'
import x from '@/components/mixins/mixin'

import BaseListPage from '@/components/organisms/BaseListPage'
import BaseOperationTooltip from '@/components/molecules/BaseOperationTooltip'
import StockAdjustmentDialog from '@/components/pages/stock/StockAdjustmentDialog'

export default {
  name: 'StockAdjustmentList',
  components: {
    BaseListPage,
    BaseOperationTooltip,
    StockAdjustmentDialog
  },
  mixins: [x],
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  data () {
    return {
      dialogVisible: false,
      localIsParentLoaded: false
    }
  },
  computed: {
    define: () => define,
    ...mapState({
      listIndex: (state) => state.StockAdjustment.listIndex,
      tableSetList: (state) => state.StockAdjustment.tableSetList,
      tdLists: (state) => state.StockAdjustment.tdLists
    }),
    ...mapGetters({
      getSortedList: 'sortedList',
      getServiceSiteConfig: 'getServiceSiteConfig',
      searchConditions: 'searchConditions'
    }),
    ...mapGetters('LocalStorage', {
      sortCondition: 'sortCondition'
    }),
    name () {
      return 'stockAdjustmentList'
    },
    storePath () {
      return 'StockAdjustment'
    },
    saveKey () {
      return 'stockAdjustmentCondition'
    },
    responsePath () {
      return 'data.stockAdjustmentList.edges'
    },
    baseQuery () {
      return query.GRAPH_QL_QUERY_STOCK_ADJUSTMENT_LIST
    },
    sortedList () {
      return this.getSortedList(this.storePath, this.sortCondition(this.name))
    },
    _tableSetList () {
      return [
        { type: 'String', label: '指示管理番号', path: 'node.stockAdjustmentNo', sortable: 'adjustment' },
        { type: 'String', label: 'SKU', path: 'node.sku.code', sortable: 'adjustment' },
        { type: 'String', label: '商品名', path: 'node.sku.name', sortable: 'adjustment', width: '400px' },
        { type: 'String', label: '区分', path: 'node.category', codeName: 'd_stock_adjustment.category', sortable: 'adjustment' },
        { type: 'String', label: '理由', path: 'node.reason', codeName: 'd_stock_adjustment.reason', sortable: 'adjustment' },
        { type: 'Number', label: '数量', path: 'node.qty', sortable: 'adjustment' },
        { type: 'String', label: 'メモ', path: 'node.note', sortable: 'adjustment' },
        { type: 'Date', label: '送信日時', path: 'node.sendAt', sortable: 'adjustment' },
        { type: 'Date', label: '作成日時', path: 'node.createdAt', sortable: 'adjustment' },
        {
          itemKey: 'operation', isAvailable: true, isActive: true, fixed: 'right',
          type: 'Slot', label: '', path: 'operation', width: '60px', columnKey: 'operation', class: 'right',
          isShow: () => this.showOperation()
        }
      ]
    }
  },
  created () {
    this.$store.dispatch({
      type: 'set',
      states: {
        'StockAdjustment/tableSetList': this._tableSetList
      }
    })
  },
  methods: {
    ...mapMutations('StockAdjustment', {
      updateListItem: 'update'
    }),
    /**
     * アクションボタン
     * @param {Number} mode モード
     */
    actionCommand: async function (mode) {
      switch (mode) {
        case define.STOCK_ADJUSTMENT_MODE.NEW:
          this.dialogVisible = true
          break
        default:
          break
      }
    },
    /**
     * Tooltipの項目を返却する.
     * @param {Object} row 在庫調整情報
     * @return {Array}
     */
    getTooltipList (row) {
      return [
        {
          title: '削除',
          click: (item) => this.delete(row.node.uuid)
        }
      ]
    },
    /**
     * 在庫調整削除
     */
    delete (uuid) {
      this.$confirm('対象の未連携の在庫調整を削除します。よろしいですか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(async () => {
        try {
          this.$loadingService.show()
          // 削除APIを呼び出す
          await this.callApi(uuid)
          this.$notify.success('在庫調整を削除しました。')
          // 一覧画面データに反映する
          this.refreshList()
        } catch (error) {
          const data = _.get(error, 'response.data')
          if (data == null) {
            common.addError(error, { msg: message.MSG_502() })
          } else {
            common.addError(error, { msg: message.MSG_555() })
          }
          this.$notify.error({ error: error })
          throw error
        } finally {
          this.$loadingService.close()
        }
      }).catch(() => {})
    },
    /**
     * 削除APIを呼び出す
     */
    async callApi (uuid) {
      const url = config.EC_STOCK_ADJUSTMENT + '/' + uuid
      const method = 'delete'
      const response = await common.apiAxios(url, { method })
      return response
    },
    /**
     * 一覧画面のデータをリフレッシュする.
     */
    refreshList () {
      const queryCondition = this.searchConditions(this.storePath)
      this.updateListItem(queryCondition)
    },
    showTooltip (row) {
      return !row.node.sendAt
    },
    showOperation () {
      return this.$isFunctionShown(define.PermissionKeys.Stock.Adjustment.List.DELETE) &&
        this.tdLists.findIndex((row) => !row.node.sendAt) >= 0
    }
  }
}
</script>
